import React, { useMemo } from 'react';
import { MathJax } from 'better-react-mathjax';

const decodeHtmlEntities = (value) =>
    String(value)
        .replace(/&apos;/gi, "'")
        .replace(/&#39;/g, "'")
        .replace(/&quot;/gi, '"')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&amp;/gi, '&');

const hasMathDelimiters = (value) => /\$\$[\s\S]+?\$\$|\$[\s\S]+?\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)/.test(String(value || ''));
const containsMathMl = (value) => /<math\b[\s\S]*?<\/math>/i.test(String(value || ''));
const containsMathContent = (value) => containsMathMl(value)
    || hasMathDelimiters(value)
    || /\\(?:frac|sqrt|left|right|begin|end|matrix|sum|int|alpha|beta|gamma)\b/.test(String(value || ''))
    || /\\[a-zA-Z]+/.test(String(value || ''))
    || /[A-Za-z0-9)}\]]\s*[\^_]\s*\*?\s*(?:\{|[A-Za-z0-9\\])/.test(String(value || ''));

const containsHtmlTag = (value) => /<[a-z][\s\S]*>/i.test(String(value || ''));

const wrapBareLatexSegment = (input) => {
    const text = String(input || '');
    if (!text || hasMathDelimiters(text)) {
        return text;
    }

    const commandRegex = /\\[a-zA-Z]+/g;
    const allowedAround = /[A-Za-z0-9_{}^()+\-*/=\\\s]/;
    let rebuilt = '';
    let cursor = 0;
    let commandMatch;

    while ((commandMatch = commandRegex.exec(text)) !== null) {
        const commandIndex = commandMatch.index;
        if (commandIndex < cursor) {
            continue;
        }

        let start = commandIndex;
        while (start > cursor && allowedAround.test(text[start - 1])) {
            start -= 1;
        }

        let end = commandIndex + commandMatch[0].length;
        while (end < text.length && allowedAround.test(text[end])) {
            end += 1;
        }

        const rawSegment = text.slice(start, end);
        const trimmedSegment = rawSegment.trim();

        if (!trimmedSegment || !/\\[a-zA-Z]+/.test(trimmedSegment)) {
            continue;
        }

        const leading = rawSegment.slice(0, rawSegment.indexOf(trimmedSegment));
        const trailing = rawSegment.slice(rawSegment.indexOf(trimmedSegment) + trimmedSegment.length);

        rebuilt += text.slice(cursor, start);
        rebuilt += `${leading}$${trimmedSegment}$${trailing}`;
        cursor = end;
    }

    if (!rebuilt) {
        return text;
    }

    rebuilt += text.slice(cursor);
    return rebuilt;
};

const autoWrapBareLatexSystems = (value) => {
    const input = String(value || '');

    if (!input || hasMathDelimiters(input)) {
        return input;
    }

    if (!/\\begin\{(?:matrix|cases|aligned)\}/.test(input)) {
        return input;
    }

    // Tu dong boc cac he phuong trinh DOCX dang LaTeX tran de parser nhan dien la math block.
    return input.replace(
        /(\\left(?:\\\{|\\\(|\{|\()?[\s\S]*?\\begin\{(?:matrix|cases|aligned)\}[\s\S]*?\\end\{(?:matrix|cases|aligned)\}(?:[\s\S]*?\\right(?:\\\}|\\\)|\.)?)?)/g,
        (segment) => {
            const trimmed = String(segment || '').trim();
            if (!trimmed) {
                return segment;
            }

            if (hasMathDelimiters(trimmed)) {
                return segment;
            }

            return `$$${trimmed}$$`;
        }
    );
};

const autoWrapBareLatexFragments = (value) => {
    const input = String(value || '');
    if (!input) {
        return input;
    }

    // Chi xu ly cac doan text thuần; cac doan da co delimiter thi giu nguyen.
    const splitByMath = /(\\\[[\s\S]+?\\\]|\$\$[\s\S]+?\$\$|\\\([\s\S]+?\\\)|\$[\s\S]+?\$)/g;
    return input
        .split(splitByMath)
        .map((segment, index) => {
            if (index % 2 === 1) {
                return segment;
            }
            return wrapBareLatexSegment(segment);
        })
        .join('');
};

// Chuan hoa mot so cu phap TeX tu Word/DOCX de KaTeX de parse hon.
const normalizeLatex = (value) => {
    let normalized = String(value || '').trim();

    if (!normalized || !normalized.includes('\\')) {
        return normalized;
    }

    // Word/DOCX co the chen break HTML vao trong cong thuc; doi sang khong ký tự.
    if (/<\s*br\s*\/?\s*>/i.test(normalized)) {
        normalized = normalized.replace(/<\s*br\s*\/?\s*>/gi, ' ');
    }

    // Word hay chen lenh can le \\hfill vao giua he phuong trinh.
    if (normalized.includes('\\hfill')) {
        normalized = normalized.replace(/\\hfill/g, ' ');
    }

    // Chuyen cac macro amsmath de KaTeX an toan hon.
    if (normalized.includes('\\hdots') || /\\dots(?:b|c|i|m|o)\b/.test(normalized)) {
        normalized = normalized.replace(/\\hdots\b/g, '\\cdots');
        normalized = normalized.replace(/\\dots(?:b|c|i|m|o)\b/g, '\\dots');
    }

    // DOCX hay nhan doi delimiter khi cong thuc co dau gia tri |_{a}^{b}.
    normalized = normalized.replace(/\\left\s*\(\s*\\left\s*\(/g, '\\left(');
    normalized = normalized.replace(/\\left\s*\[\s*\\left\s*\[/g, '\\left[');
    normalized = normalized.replace(/\\left\s*\\\{\s*\\left\s*\\\{/g, '\\left\\{');
    normalized = normalized.replace(/\\right\s*\)\s*\\right\s*\)/g, '\\right)');
    normalized = normalized.replace(/\\right\s*\]\s*\\right\s*\]/g, '\\right]');
    normalized = normalized.replace(/\\right\s*\\\}\s*\\right\s*\\\}/g, '\\right\\}');

    // Chuyen dang DOCX "\left(...\right)\right|_{a}^{b}" ve dang KaTeX co the parse.
    normalized = normalized.replace(
           /\\left\s*\(([\s\S]*?)\\right\s*\)\s*\\right\s*(?:\||\\vert|∣)\s*_(\{[^}]*\}|[^\s^]+)\s*\^(\{[^}]*\}|[^\s]+)(?=\s*$)/,
           (match, inner, lower, upper) => `\\left. {\\left(${inner}\\right)} \\right|_${lower}^${upper}`
    );

    // Chuyen cu phap matrix kieu cu ve dang KaTeX pho bien.
    if (normalized.includes('\\matrix') || normalized.includes('\\endmatrix')) {
        normalized = normalized.replace(/\\matrix\s*\{/g, '\\begin{matrix}');
        normalized = normalized.replace(/\\endmatrix\b/g, '\\end{matrix}');
    }

    // Chuan hoa truong hop he phuong trinh mo ngoac nhon ben trai.
    if (normalized.includes('\\begin{matrix}')) {
        normalized = normalized.replace(/\\\{\s*\\begin\{matrix\}/g, '\\left\\{\\begin{matrix}');
    }
    if (/\\left\\\{\\begin\{matrix\}/.test(normalized) && /\\end\{matrix\}/.test(normalized) && !/\\right\./.test(normalized)) {
        normalized = normalized.replace(/\\end\{matrix\}/, '\\end{matrix}\\right.');
    }

    // DOCX co the tao dang "\{ \begin{matrix}" thay vi "\left\{\begin{matrix}".
    if (normalized.includes('\\begin{matrix}')) {
        normalized = normalized.replace(/\\\{\s*\\begin\{matrix\}/g, '\\left\\{\\begin{matrix}');
    }

    // Chuan hoa truong hop nguoi dung go \left{...\right} (thieu escape dau ngoac).
    if (normalized.includes('\\left') || normalized.includes('\\right')) {
        normalized = normalized.replace(/\\left\s*\{/g, '\\left\\{');
        normalized = normalized.replace(/\\right\s*\}/g, '\\right\\}');
    }

    // Gom cac token \left lap do DOCX convert loi (vd: \left\left( -> \left().
    if (normalized.includes('\\left')) {
        normalized = normalized.replace(/(?:\\left\s*){2,}(?=\\?[({])/g, '\\left');
    }

    // DOCX co the sinh du token \right (vd: ...\right.\right) lam KaTeX bao loi parse.
    if (normalized.includes('\\right')) {
        normalized = normalized.replace(/(?:\\right\s*\.?\s*){2,}/g, '\\right.');
        normalized = normalized.replace(/\\right\s*$/g, '\\right.');
    }

    // Loai bo dau cach du thua quanh dau $ neu bi tach boi xuong dong/format.
    normalized = normalized.replace(/^\$\s+/, '$').replace(/\s+\$$/, '$');

    return normalized;
};

const normalizeMathMlDisplay = (value, renderBlockAsInline) => String(value || '').replace(
    /<math\b([^>]*)>/gi,
    (match, attrs) => {
        const nextDisplay = renderBlockAsInline
            ? 'inline'
            : /\bdata-docx-block\s*=\s*(['"])true\1/i.test(attrs || '')
                ? 'block'
                : 'inline';

        if (/\bdisplay\s*=/.test(attrs || '')) {
            return `<math${String(attrs || '').replace(/\bdisplay\s*=\s*(['"])[^'"]*\1/i, `display="${nextDisplay}"`)}>`;
        }

        return `<math${attrs || ''} display="${nextDisplay}">`;
    }
);

const isBlockMathMl = (value) => /\bdisplay\s*=\s*(['"])block\1/i.test(String(value || ''))
    || /\bdata-docx-block\s*=\s*(['"])true\1/i.test(String(value || ''));

const getMathErrorMessage = (error) => {
    if (!error) {
        return 'Invalid math syntax';
    }

    if (typeof error === 'string') {
        return error;
    }

    return error.message || 'Invalid math syntax';
};

const formatFormulaForErrorDisplay = (formula) => {
    const value = String(formula || '').replace(/\s+/g, ' ').trim();
    if (!value) {
        return '(empty formula)';
    }

    return value.length > 180 ? `${value.slice(0, 177)}...` : value;
};

const buildTextMarkup = (value) => String(value || '')
    .replace(/&lt;br\s*\/?&gt;/gi, '<br/>')
    .replace(/\\n/g, '<br/>')
    .replace(/\n/g, '<br/>');

const splitIntoParts = (value, renderBlockAsInline) => {
    const decoded = decodeHtmlEntities(String(value || ''))
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/&lt;br\s*\/?&gt;/gi, '<br/>');

    const normalizedInput = autoWrapBareLatexSystems(autoWrapBareLatexFragments(decoded));
    const parts = [];
    const mathRegex = /<math\b[\s\S]*?<\/math>|\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$|\\\(([\s\S]+?)\\\)|\$([\s\S]+?)\$/gi;
    let lastIndex = 0;
    let match;

    while ((match = mathRegex.exec(normalizedInput)) !== null) {
        if (match.index > lastIndex) {
            parts.push({
                type: 'text',
                content: normalizedInput.slice(lastIndex, match.index),
            });
        }

        const rawMatch = match[0] || '';
        if (/^<math\b/i.test(rawMatch)) {
            const normalizedMathMl = normalizeMathMlDisplay(rawMatch, renderBlockAsInline);
            parts.push({
                type: 'mathml',
                content: normalizedMathMl,
                isBlock: !renderBlockAsInline && isBlockMathMl(normalizedMathMl),
            });
        } else {
            const isBlock = Boolean(match[1] || match[2]) && !renderBlockAsInline;
            const latexContent = normalizeLatex(match[1] || match[2] || match[3] || match[4]);
            parts.push({
                type: 'latex',
                content: latexContent,
                isBlock,
            });
        }

        lastIndex = match.index + rawMatch.length;
    }

    if (lastIndex < normalizedInput.length) {
        parts.push({
            type: 'text',
            content: normalizedInput.slice(lastIndex),
        });
    }

    return parts;
};

class MathErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.content !== this.props.content || prevProps.type !== this.props.type) {
            this.setState({ error: null });
        }
    }

    render() {
        if (this.state.error) {
            const { content, type } = this.props;
            const formulaSource = type === 'latex'
                ? formatFormulaForErrorDisplay(content)
                : formatFormulaForErrorDisplay(String(content || '').replace(/<[^>]+>/g, ' '));
            const errorMessage = getMathErrorMessage(this.state.error);
            return (
                <span className="text-danger" title={`Math Error: ${errorMessage}`}>
                    Formula: {formulaSource} | Error: {errorMessage}
                </span>
            );
        }
        return this.props.children;
    }
}

function MathFormula({ type, content, isBlock }) {
    const formulaMarkup = type === 'latex'
        ? (isBlock ? `\\[${content}\\]` : `\\(${content}\\)`)
        : content;

    const mathContent = type === 'latex'
        ? formulaMarkup
        : <span dangerouslySetInnerHTML={{ __html: formulaMarkup }} />;

    if (isBlock) {
        return (
            <MathErrorBoundary content={content} type={type}>
                <div className="quiz-block-math">
                    <MathJax dynamic hideUntilTypeset="first">
                        {mathContent}
                    </MathJax>
                </div>
            </MathErrorBoundary>
        );
    }

    return (
        <MathErrorBoundary content={content} type={type}>
            <MathJax inline dynamic hideUntilTypeset="first">
                {mathContent}
            </MathJax>
        </MathErrorBoundary>
    );
}

/**
 * MathText - Component hiển thị văn bản có chứa công thức toán học LaTeX.
 *
 * Hỗ trợ các dạng cú pháp:
 *  - Inline math:  $...$   → hiển thị công thức trong dòng văn bản
 *  - Block math:  $$...$$
 *  - Inline math:  \(...\) → hiển thị công thức trong dòng văn bản
 *  - Block math:  \[...\]
 *
 * Mặc định, block delimiter vẫn render cùng dòng để phù hợp nội dung quiz.
 * Có thể bật render block riêng dòng bằng prop renderBlockAsInline={false}.
 *
 * Phần văn bản thường sẽ xuống dòng theo ký tự '\n'.
 * Nếu xảy ra lỗi parse LaTeX, hiển thị "[Math Error]" thay vì crash.
 *
 * @param {string} text - Chuỗi văn bản đầu vào, có thể chứa công thức LaTeX.
 */
export default function MathText({ text, renderBlockAsInline = true }) {
    const parts = useMemo(
        () => splitIntoParts(text, renderBlockAsInline),
        [text, renderBlockAsInline]
    );

    if (!text || parts.length === 0) {
        return <span>{text || ''}</span>;
    }

    if (!containsMathContent(text)) {
        if (containsHtmlTag(text)) {
            return <span dangerouslySetInnerHTML={{ __html: buildTextMarkup(text) }} />;
        }

        return <span>{text || ''}</span>;
    }

    return (
        <>
            {parts.map((part, index) => {
                if (part.type === 'text') {
                    const html = buildTextMarkup(part.content);
                    if (!html) {
                        return null;
                    }

                    return <span key={`text-${index}`} dangerouslySetInnerHTML={{ __html: html }} />;
                }

                return (
                    <MathFormula
                        key={`math-${index}`}
                        type={part.type}
                        content={part.content}
                        isBlock={part.isBlock}
                    />
                );
            })}
        </>
    );
}


